import { create } from 'zustand';
import { cartApi } from '../services/cartApi';
import type { CartItem } from '@printtat/shared';

interface CartState {
  items: CartItem[];
  isLoading: boolean;

  fetchCart: () => Promise<void>;
  addItem: (designId: string, variantId: string, quantity?: number) => Promise<CartItem>;
  addCatalogItem: (catalogProductId: string, variantId: string, quantity?: number) => Promise<CartItem>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;

  itemCount: () => number;
  subtotal: () => number;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  isLoading: false,

  fetchCart: async () => {
    set({ isLoading: true });
    try {
      const response = await cartApi.getCart();
      set({ items: response.items });
    } catch (err) {
      console.error('Failed to fetch cart:', err);
    } finally {
      set({ isLoading: false });
    }
  },

  addItem: async (designId, variantId, quantity = 1) => {
    const item = await cartApi.addToCart({ designId, variantId, quantity });
    await get().fetchCart();
    return item;
  },

  addCatalogItem: async (catalogProductId, variantId, quantity = 1) => {
    const item = await cartApi.addToCart({ catalogProductId, variantId, quantity });
    await get().fetchCart();
    return item;
  },

  updateQuantity: async (itemId, quantity) => {
    // Optimistic update
    set((state) => ({
      items: state.items.map((item) =>
        item.id === itemId ? { ...item, quantity } : item,
      ),
    }));
    try {
      await cartApi.updateQuantity(itemId, quantity);
    } catch (err) {
      // Revert on failure
      await get().fetchCart();
      throw err;
    }
  },

  removeItem: async (itemId) => {
    // Optimistic remove
    const prevItems = get().items;
    set((state) => ({
      items: state.items.filter((item) => item.id !== itemId),
    }));
    try {
      await cartApi.removeItem(itemId);
    } catch (err) {
      // Revert on failure
      set({ items: prevItems });
      throw err;
    }
  },

  clearCart: async () => {
    set({ items: [] });
    try {
      await cartApi.clearCart();
    } catch (err) {
      await get().fetchCart();
      throw err;
    }
  },

  itemCount: () => get().items.reduce((sum, item) => sum + item.quantity, 0),

  subtotal: () =>
    get().items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0),
}));
